import {useMutation, useQueryClient} from '@tanstack/react-query';
import {
  bulkRemoveRecruitmentsFromCache,
  type RecruitmentCacheSnapshot,
} from '@/utils/cache-updaters/recruitments';
import type {
  BulkRemoveRecruitmentsParams,
  BulkRemoveRecruitmentsResponse,
} from '@/types/recruitment';
import recruitmentsFetchers from '@/api/fetchers/recruitments';
import {recruitmentsQueryKey} from '@/hooks/queries/use-recruitments-query';

function useBulkRemoveRecruitments() {
  const queryClient = useQueryClient();

  return useMutation<
    BulkRemoveRecruitmentsResponse,
    Error,
    BulkRemoveRecruitmentsParams,
    RecruitmentCacheSnapshot[]
  >({
    mutationKey: [recruitmentsQueryKey, 'bulkRemove'],
    mutationFn: recruitmentsFetchers.bulkRemove,
    onMutate: (variables: BulkRemoveRecruitmentsParams) => {
      return bulkRemoveRecruitmentsFromCache({
        agencyId: variables.agencyId,
        recruitmentIds: variables.crmRecruitmentIds,
      });
    },
    onError: (_err, _variables, context) => {
      if (context) {
        context.forEach(({queryKey, previousData}) => {
          queryClient.setQueryData(queryKey, previousData);
        });
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [recruitmentsQueryKey, variables.agencyId],
      });
    },
  });
}

export default useBulkRemoveRecruitments;
